import React, { useMemo, useRef, useState } from 'react'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { BadgeInfo, BadgeSuccess, BadgeWarning } from '../../../../../shared/component/badge'
import { Menu, MenuDelete, MenuEdit, MenuExport, MenuItem, MenuRecycle, MenuTableSettingToggle } from '../../../../../shared/menu/menu'
import { CardFullHeightScrollY } from '../../../../../shared/card/card-full-height-scroll-y'
import { useApiListTradeshow } from '../../../../../api-new/state/content'
import { IconActivityLog, IconCopy, IconLaunch, IconPlus, IconTradeShow } from '../../../../../shared/icon/icon'
import { useAuditCapabilities } from '../../../../../shared/audit/use-audit-capabilities'
import { ModalDialog, useModalActions } from '../../../../../shared/component/modal-dialog'
import { CopyValue } from '../../../../../shared/sso-workspace/copy-value'
import '../../../../../shared/sso-workspace/sso-workspace.scss'
import { useRoleOrgTradeshow } from '../../../../../role/hook/role-org'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ColumnText, ColumnDate, ColumnMenu, ColumnSelector } from '../../../../../shared/table-builder/table-columns'

export const TradeShowList = () => {
	const apiListTradeshow = useApiListTradeshow()

	const modalActions = useModalActions('copylink')

	const roleOrgTradeshow = useRoleOrgTradeshow()
	const caps = useAuditCapabilities()

	const handleReady = async () => await apiListTradeshow.get()
	const handleDestroyed = () => apiListTradeshow.init()

	const [copyIdentifier, updateCopyIdentifier] = useState()

	const handleCopy = (identifier) => {
		updateCopyIdentifier(identifier)
		modalActions.show()
	}

	const handleOpen = (identifier) => window.open(`/external/tradeshow/${identifier}/register`)

	const tableRef = useRef()
	const handleExportCsv = () => tableRef.current.exportCsv('tradeshows')
	const handleTableSettingToggle = () => tableRef.current.toggleSideBar()

	const HeaderMenu = () => (
		<Menu>
			<MenuItem text='add' Icon={IconPlus} to='/org/tradeshow/list/add' show={roleOrgTradeshow.accessCreate} pin />
			<MenuRecycle to='/org/tradeshow/list/recycle' show={roleOrgTradeshow.accessDelete} />
			<MenuExport onClick={handleExportCsv} show />
			<MenuTableSettingToggle onClick={handleTableSettingToggle} show />
			<MenuItem Icon={IconActivityLog} text="audit log" to="/org/audit-log?type=trade_shows" show={caps.canAudit} />
		</Menu>
	)

	return (
		<React.Fragment>
			<CopyLinkModal identifier={copyIdentifier} />
			<BreadcrumbView Icon={IconTradeShow} text='TRADE SHOWS'>
				<CardFullHeightScrollY title='tradeshows' HeaderMenu={HeaderMenu}>
					<TableAgClient ref={tableRef} apiList={apiListTradeshow} onReady={handleReady} onDestoryed={handleDestroyed}>
						<ColumnText field='name' />
						<ColumnDate field='startOn' header='starts on' />
						<ColumnDate field='endOn' header='ends on' />
						<ColumnSelector field='status' selector={(row) => <TradeShowStatus tradeShow={row} />} />
						<ColumnMenu
							menu={(row) => (
								<Menu small>
									<MenuEdit to={`/org/tradeshow/list/edit/${row.id}`} show={roleOrgTradeshow.accessUpdate} />
									<MenuItem Icon={IconCopy} text='copy link' onClick={() => handleCopy(row.identifier)} show />
									<MenuItem Icon={IconLaunch} text='launch' onClick={() => handleOpen(row.identifier)} show />
									<MenuDelete to={`/org/tradeshow/list/edit/${row.id}/delete`} show={roleOrgTradeshow.accessDelete} />
								</Menu>
							)}
						/>
					</TableAgClient>
				</CardFullHeightScrollY>
			</BreadcrumbView>
		</React.Fragment>
	)
}

const TradeShowStatus = ({ tradeShow }) => {
	const status = useMemo(() => {
		let now = Date.now()
		let startOn = Date.parse(tradeShow.startOn)
		let endOn = Date.parse(tradeShow.endOn)

		if (tradeShow.closed) {
			return <BadgeInfo value='closed' />
		} else if (!startOn && !endOn) {
			return <BadgeSuccess value='open' />
		} else if (tradeShow.activeUntilEnd && now < endOn) {
			return <BadgeSuccess value='open' />
		} else if (now > startOn && now < endOn) {
			return <BadgeSuccess value='open' />
		} else {
			return <BadgeWarning value='inactive' />
		}
	}, [tradeShow])

	return <React.Fragment>{status}</React.Fragment>
}

const CopyLinkModal = ({ identifier }) => {
	const url = useMemo(
		() => `${process.env.REACT_APP_SITE}/external/tradeshow/${identifier}?email=EMAIL&first-name=FIRST_NAME&last-name=LAST_NAME`,
		[identifier]
	)

	// The shared CopyValue (owner 2026-10-07): Copy that Safari allows. No
	// Open: the link carries placeholders (EMAIL, FIRST_NAME) to fill first.
	return (
		<ModalDialog target='copylink' header='Open Link'>
			<div className='sw-workspace'>
				<CopyValue id='tradeshow-link' label='Trade show link' value={url} />
			</div>

			<table className='table'>
				<tbody>
					<tr>
						<th>Argument</th>
						<th>Type</th>
					</tr>
					<tr>
						<td>email</td>
						<td>string</td>
					</tr>
					<tr>
						<td>first-name</td>
						<td>string</td>
					</tr>
					<tr>
						<td>last-name</td>
						<td>string</td>
					</tr>
				</tbody>
			</table>
		</ModalDialog>
	)
}
